import _ from "lodash";

import { AllowListType } from "../PHIAllowListPage/types";
import { AccountMetadata, LocalStorageProject } from "../reducers/environments";
import {
  LoadedDestinationInfo,
  PropertySensitivityLevels,
  SensitivityLevel,
} from "../types/global";
import { isAccountInHIPAAModeV2 } from "../util";
import destinationPropertyMetadataJSON from "./destinationPropertyMetadata.json";
import { DestinationKey } from "./types";

export enum PropertyGroup {
  Required = "required",
  Recommended = "recommended",
  Attribution = "attribution",
  UTM = "utm",
  ViewThrough = "view-through",
}

// DestinationPropertyMetadata is metadata describing an event, user, or group
// property in the context of a particular destination. For example the "$gclid"
// property has particular significance for Google Ads.
export interface DestinationPropertyMetadata {
  property: string;
  // propertyGroup is used for presenting properties to customers in groups of
  // related properties. There is a group for required properties, a group for
  // recommended properties. There may be additional groups of recommended
  // properties that are functionally interrelated.
  propertyGroup: PropertyGroup;
  propertyType: AllowListType;
  description: string;
  // alternateName is used to support legacy names for required properties. The
  // destination is considered valid if either the property or the alternate
  // name are allowed.
  alternateName?: string;
}

export function destinationHasPropertyMetadata(
  destinationKey: DestinationKey
): boolean {
  return destinationPropertyMetadataJSON[destinationKey];
}

// Returns all the property metadata in a given property group for a destination.
// Returns empty array if there are no property metadata for the destination.
export function getPropertyGroupMetadata(
  destinationKey: DestinationKey,
  propertyGroup: PropertyGroup
): DestinationPropertyMetadata[] {
  const propertyMetadataList: DestinationPropertyMetadata[] =
    destinationPropertyMetadataJSON[destinationKey] || [];
  return _.filter(
    propertyMetadataList,
    (propertyMetadata) => propertyMetadata.propertyGroup === propertyGroup
  );
}

export function getPropertySensitivityLevelsForDestination(
  destination: LoadedDestinationInfo,
  propertyType: AllowListType
): PropertySensitivityLevels {
  switch (propertyType) {
    case AllowListType.Event:
      return destination.event_property_sensitivity_levels ?? {};
    case AllowListType.User:
      return destination.user_property_sensitivity_levels ?? {};
    case AllowListType.Group:
      return destination.group_property_sensitivity_levels ?? {};
  }
}

export function isPropertyAllowedForSensitivityLevels(
  propertyMetadata: DestinationPropertyMetadata,
  allowList: PropertySensitivityLevels
): boolean {
  if (
    propertyMetadata.alternateName &&
    allowList[propertyMetadata.alternateName] === SensitivityLevel.NotSensitive
  ) {
    return true;
  }

  return allowList[propertyMetadata.property] === SensitivityLevel.NotSensitive;
}

export function isPropertyAllowedForProject(
  propertyMetadata: DestinationPropertyMetadata,
  project: LocalStorageProject
): boolean {
  let sensitivityLevels: PropertySensitivityLevels;
  switch (propertyMetadata.propertyType) {
    case AllowListType.Event:
      sensitivityLevels = project.eventPropertySensitivityLevels;
      break;
    case AllowListType.User:
      sensitivityLevels = project.userPropertySensitivityLevels;
      break;
    case AllowListType.Group:
      sensitivityLevels = project.groupPropertySensitivityLevels;
      break;
  }

  return isPropertyAllowedForSensitivityLevels(
    propertyMetadata,
    sensitivityLevels
  );
}

export function isPropertyAllowedForDestination(
  propertyMetadata: DestinationPropertyMetadata,
  destination: LoadedDestinationInfo
): boolean {
  return isPropertyAllowedForSensitivityLevels(
    propertyMetadata,
    getPropertySensitivityLevelsForDestination(
      destination,
      propertyMetadata.propertyType
    )
  );
}

// isPropertyAllowed returns true iff the property described by the given
// propertyMetadata is allowed for the given destination. It accounts for legacy
// alternate property names, and for if the property is on the project-level
// allowlist.
export function isPropertyAllowed(
  propertyMetadata: DestinationPropertyMetadata,
  project: LocalStorageProject,
  destination: LoadedDestinationInfo
): boolean {
  return (
    isPropertyAllowedForProject(propertyMetadata, project) ||
    isPropertyAllowedForDestination(propertyMetadata, destination)
  );
}

export function areAllRequiredPropertiesAllowed(
  accountMetadata: AccountMetadata,
  project: LocalStorageProject,
  destination: LoadedDestinationInfo
): boolean {
  // Per-destination allowlisting is only available in HIPAA mode V2. If HIPAA
  // Mode V1, or if not in HIPAA mode at all, we can say that all required
  // properties are allowed.
  if (!isAccountInHIPAAModeV2(accountMetadata)) {
    return true;
  }

  if (destination.has_baa) {
    return true;
  }

  const destinationKey = destination.name;
  const propertyMetadataList = getPropertyGroupMetadata(
    destinationKey,
    PropertyGroup.Required
  );
  return _.every(propertyMetadataList, (propertyMetadata) =>
    isPropertyAllowed(propertyMetadata, project, destination)
  );
}
